import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack } from "@mui/joy";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { pcSideTabAtom } from "../../recoil/pcSideTabAtom";
import { decideInitialRefund } from "../../utils/refundPolicyUtils";
import FullPageWithHeaderAndProgressV2 from "../container/FullPageWithHeaderAndProgressV2";
import CustomModal from "../../components/common/CustomModal";
import CustomTypography from "../../components/common/CustomTypography";
import TextareaRefund from "./TextareaRefund";
import { theme } from "../../styles/theme";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import useBottomSafePadding from "../../hooks/useBottomSafePadding";
import useOpenTask from "../../hooks/useOpenTask";
import useClose from "../../hooks/useClose";
import { useChannelTalkContext } from "../../contexts/ChannelTalkContext";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";

/**
 * refundPolicyType "e" 전용 환불 설문 화면.
 *
 * 환불 정책/기능은 "d" 와 완전히 동일하되, "메일로 접수해주세요" 안내 팝업 직전에
 * 환불 사유를 묻는 설문 단계를 거친다.
 *
 * 흐름:
 *  - step 0: 환불 사유 단일 선택 리스트 (탭 시 즉시 다음 단계)
 *  - step 1(사유 1~7): 사유별 안내(설득) 화면 → "환불 신청 접수하기" → 메일 접수 안내 팝업
 *  - 기타: 자유 입력(textarea) → "환불 신청 접수하기" → 메일 접수 안내 팝업
 *
 * 진입 시 decideInitialRefund 로 자격을 재검증한다. EMAIL 이 아니면(결제 없음/최초 이용
 * 아님/이미 환불/기간 만료) 해당 안내 팝업만 띄우고 종료한다.
 *
 * 선택한 사유·자유 입력값은 로컬에서만 사용하며 서버로 전송하지 않는다. (요청사항)
 * 본문 속 밑줄 강조 링크: "앱 잠금 기능을 설정"·"알림은 해제하거나 변경"은 각각 나 탭의
 * 앱 잠금/알림 설정(openTask)을 열고, "일시정지" 류는 일시정지 예약 페이지로
 * 이동하며, "고객센터" 류는 채널톡(openChat)을 연다.
 */

// 밑줄 강조 텍스트. onClick 이 있으면 동작 링크, 없으면 강조 스타일만.
function Em({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        color: theme.vars.palette.Content.Highlight,
        textDecoration: "underline",
        textUnderlineOffset: "2px",
        cursor: onClick ? "pointer" : "inherit",
      }}
    >
      {children}
    </Box>
  );
}

function ReasonTitle({ children }: { children: React.ReactNode }) {
  return (
    <CustomTypography
      variant="Subtitle"
      fontWeight={700}
      sx={{ color: theme.vars.palette.Content.Highlight }}
    >
      {children}
    </CustomTypography>
  );
}

function ReasonBody({ children }: { children: React.ReactNode }) {
  return (
    <CustomTypography
      variant="Subtitle"
      fontWeight={500}
      sx={{ color: theme.vars.palette.Content.Strong }}
    >
      {children}
    </CustomTypography>
  );
}

type Reason = {
  key: string;
  label: string;
  content: React.ReactNode;
};

const ETC_KEY = "etc";

// 사유 1~7: 사유별 안내(설득) 화면.
// "앱 잠금 설정"·"알림 변경"은 설정 페이지(openTask), "일시정지"는 예약 페이지(openPause),
// "고객센터" 문의는 채널톡(openChat) 콜백을 받는다.
function getReasons(
  openAppLock: () => void,
  openNotification: () => void,
  openPause: () => void,
  openChat: () => void
): Reason[] {
  return [
    {
      key: "privacy",
      label: "개인정보가 걱정돼요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>모든 것은 안전하게 보호됩니다.</ReasonTitle>
          <ReasonBody>
            디스턴싱에서 오가는 이야기는 오직 나와 담당 코치만 확인할 수
            있습니다.
          </ReasonBody>
          <ReasonBody>
            모든 코치는 비밀보장 원칙에 대한 책임과 의무를 지키고 있습니다.
          </ReasonBody>
          <ReasonBody>
            원하신다면 <Em onClick={openAppLock}>앱 잠금 기능을 설정</Em>하여 내
            정보를 더욱 안전하게 보호할 수도 있습니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "daily",
      label: "매일 기록하기가 부담돼요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>
            생각기록지는 매일 작성하지 않아도 괜찮습니다.
          </ReasonTitle>
          <ReasonBody>
            생각기록지는 배운 디스턴싱 기술을 나의 경험에 적용하며 연습해보는
            가장 효과적인 도구 중 하나입니다.
          </ReasonBody>
          <ReasonBody>
            만약 디스턴싱을 적용할 만한 경험이 없는 날에는 생각기록지를 작성을
            건너 뛰어도 됩니다.
          </ReasonBody>
          <ReasonBody>
            하루를 돌아보는 생각기록지{" "}
            <Em onClick={openNotification}>알림은 해제하거나 변경</Em>할 수
            있습니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "pause",
      label: "당분간 프로그램을 이어가기 어려워요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>잠시 쉬어가도 괜찮습니다.</ReasonTitle>
          <ReasonBody>
            내 마음을 들여다보는 일은 결코 쉬운 일이 아닙니다. 생각보다 많은
            에너지와 용기를 필요로 합니다.
          </ReasonBody>
          <ReasonBody>
            외부 환경의 변화로 인해, 지치고 힘든 시기에는 잠시 멈추고 쉬어가는
            것도 현명한 선택입니다.
          </ReasonBody>
          <ReasonBody>
            원하신다면 <Em onClick={openPause}>프로그램을 일시정지</Em>하여 잠시
            쉬어갈 수 있습니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "schedule",
      label: "코치와 시간을 맞추기 어려워요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>코치의 시간에 맞출 필요는 없습니다.</ReasonTitle>
          <ReasonBody>
            디스턴싱은 약속된 시간에 진행되는 실시간 상담이 아닙니다.
          </ReasonBody>
          <ReasonBody>
            편한 시간에 언제든 활동지를 진행하고, 궁금한 점이 있다면 메시지를
            남겨둘 수 있습니다.
          </ReasonBody>
          <ReasonBody>
            그 뒤 코치가 다음 활동과 피드백을 전달드리는 비실시간 방식으로
            프로그램이 진행됩니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "other_treatment",
      label: "다른 치료를 받아보기로 했어요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>다른 치료와 병행할 수 있습니다.</ReasonTitle>
          <ReasonBody>
            다양한 연구에서 인지행동치료는 약물치료에 준하는 효과를 보이는
            것으로 알려져 있습니다.
          </ReasonBody>
          <ReasonBody>
            또한 단순한 증상 완화를 넘어, 스스로 마음을 이해하고 돌보는 힘을
            기르는 과정에 집중합니다.
          </ReasonBody>
          <ReasonBody>
            상황에 따라 상담이나 치료 등의 다른 치료 방법과 상호보완적으로 함께
            이용할 수 있습니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "resolved",
      label: "문제가 어느 정도 해결되었어요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>나아졌다고 느끼신다니 다행입니다.</ReasonTitle>
          <ReasonBody>
            우리의 마음은 외부 환경과 시간의 흐름에 많은 영향을 받습니다.
          </ReasonBody>
          <ReasonBody>
            잠시 괜찮아졌다고 느껴지다가도, 다시 흔들리는 순간이 찾아오기도
            합니다.
          </ReasonBody>
          <ReasonBody>
            당장은 문제가 해결되어 조금 지켜보고 싶다면, 프로그램을 완전히
            종료하기 전에 <Em onClick={openPause}>잠시 일시정지</Em>하며 지켜볼
            수 있습니다.
          </ReasonBody>
        </Stack>
      ),
    },
    {
      key: "coach",
      label: "코치 선생님과 맞지 않아요",
      content: (
        <Stack spacing={2}>
          <ReasonTitle>코치와의 호흡 역시 중요한 부분입니다.</ReasonTitle>
          <ReasonBody>
            디스턴싱의 코치는 동일한 가이드를 따르지만, 소통 방식이나 분위기에는
            조금씩 차이가 있을 수 있습니다.
          </ReasonBody>
          <ReasonBody>
            원칙적으로 코치 변경이 어렵지만, 진행이 어려운 특별한 사유가 있다면{" "}
            <Em onClick={openChat}>고객센터로 문의</Em>해주세요.
          </ReasonBody>
        </Stack>
      ),
    },
  ];
}

// 하단 고정 빨간 CTA (환불 신청 접수하기)
function BottomDangerButton({
  onClick,
  text,
}: {
  onClick: () => void;
  text: string;
}) {
  const pb = useBottomSafePadding("16px");
  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        bottom: 0,
        width: "100%",
        zIndex: 1200,
        background: `linear-gradient(180deg, rgba(246, 249, 250, 0) 0%, ${theme.vars.palette.Background.Level1} 20%)`,
        px: "20px",
        pt: 2,
        pb,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Button
        variant="Action"
        color="danger"
        size="lg"
        sx={{
          maxWidth: "620px",
          width: "100%",
          height: "54px",
          fontWeight: 700,
        }}
        onClick={onClick}
      >
        {text}
      </Button>
    </Box>
  );
}

export default function RefundSurveyTypeE() {
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const isInappWebview = useIsInappWebview();

  const openAppLock = useOpenTask({ taskKey: "security-settings" });
  const openNotification = useOpenTask({ taskKey: "notification-settings" });
  const openPauseTask = useOpenTask({ taskKey: "subscription-pause" });
  const { openChat } = useChannelTalkContext();
  // PC(비웹뷰)는 Home 이 풀페이지 복귀 시 기본 탭으로 리다이렉트하므로, 닫기/안착은
  // useClose(웹뷰 navigate_back; PC setPCSideTab("home")+navigate("/"))로 탭=홈과 URL 을 일치시킨다.
  const close = useClose();
  const setPCSideTab = useSetRecoilState(pcSideTabAtom);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // step0 사유 탭 시 다음 화면 전환 직전 primary 강조를 잠깐 보여주는 과도 상태. (Acquisition_1 패턴)
  const [tappedKey, setTappedKey] = useState<string | null>(null);
  const [etcText, setEtcText] = useState<string>("");
  const [etcFocused, setEtcFocused] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  // PC(웹)에서 앱잠금/알림 설정 링크 탭 시 "모바일 앱에서 가능" 안내 팝업.
  const [mobileOnlyModal, setMobileOnlyModal] = useState<{
    title: string;
    subtitle: string;
  } | null>(null);

  // Mixpanel: 페이지 진입 1종 + 사유 선택 + 본문 링크 클릭 4종.
  const trackEnter = useMixpanelWeb({
    eventName: "환불 사유 설문 페이지 진입",
  });
  const trackSelectReason = useMixpanelWeb({
    eventName: "환불 사유 설문 선택",
  });
  // 접수(환불 신청 접수하기)는 선택과 구분되는 별도 이벤트. 기타는 자유 입력값을 함께 싣는다.
  const trackSubmitReason = useMixpanelWeb({
    eventName: "환불 사유 설문 기타 사유 작성",
  });
  // 링크 클릭은 어떤 사유 안내 화면에서 눌렀는지 reason 파라미터로 남긴다(렌더 시점 selectedKey).
  const trackPauseLink = useMixpanelWeb({
    eventName: "환불 사유 설문 일시정지 링크 클릭",
    params: { reason: selectedKey ?? "" },
  });
  const trackAppLockLink = useMixpanelWeb({
    eventName: "환불 사유 설문 앱잠금 링크 클릭",
    params: { reason: selectedKey ?? "" },
  });
  const trackNotificationLink = useMixpanelWeb({
    eventName: "환불 사유 설문 알림설정 링크 클릭",
    params: { reason: selectedKey ?? "" },
  });
  const trackChatLink = useMixpanelWeb({
    eventName: "환불 사유 설문 고객센터 링크 클릭",
    params: { reason: selectedKey ?? "" },
  });

  // 진입 이벤트는 마운트 1회만 (StrictMode 중복 방지).
  const enteredRef = useRef(false);
  useEffect(() => {
    if (enteredRef.current) return;
    enteredRef.current = true;
    trackEnter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // user 하이드레이션 전엔 자격 판정을 미룬다. (RefundScreen.tsx:82 패턴)
  // null 인 첫 렌더에 decideInitialRefund 가 NO_PAYMENT 로 빠져 "환불할 결제가 없어요"
  // 오모달이 번쩍이는 것을 막는다.
  if (!user) return null;

  // "e" 는 "d" 와 동일하게 최초 이용 환불 기간이 7일이다. (c 만 14일)
  const decision = decideInitialRefund({
    user: user ?? undefined,
    limitDays: 7,
  });

  // 종료(접수 완료 모달·자격미달 모달 닫기) 시 설문을 닫아 직전 — 설문을 띄웠던
  // FAQ '환불이 가능한가요?' 상세 — 로 복귀한다. 다른 링크들과 'FAQ상세 복귀'로 통일.
  // 모달이 backdrop 으로 설문을 가리므로 닫는 순간 navigate(-1) 로 설문이 사라지며
  // FAQ상세가 드러난다(시각상 "설문 닫히고 모달 → 닫으면 FAQ상세" 와 동일).
  const handleExit = () => navigate(-1);

  // 자격 미달(EMAIL 이 아님): 사유 안내 팝업만 노출 후 종료.
  if (decision.key !== "EMAIL") {
    return (
      <CustomModal
        open={true}
        onClose={handleExit}
        title={decision.title}
        subtitle={decision.subtitle}
        actions={[{ label: "확인", onClick: handleExit, variant: "Solid" }]}
      />
    );
  }

  // 본문 링크로 설정 페이지를 연다.
  // - 웹뷰: openTask(useOpenTask)로 설정을 네이티브 push 하고, 그 아래 가려진 webview 의
  //   설문 SPA 엔트리만 navigate(-1) 로 닫는다. 설정을 완료/뒤로 하면 설문을 띄웠던
  //   FAQ '환불이 가능한가요?' 상세가 드러난다. ('설문만 닫기' — 네이티브 스택의
  //   고객센터/FAQ 화면은 SPA 로 못 닫고, close_all 로 '나' 탭 루트까지 가는 건
  //   close_all→push 순서를 네이티브가 보장 못해 불가로 결론.)
  //   ※ unifiedNavigate(open_screen)는 URL 에 인증 파라미터(at/pid)를 안 붙여 설정
  //     webview 가 토큰 없이 무한 로딩 → MyPage 와 동일한 openTask(navigate 이벤트,
  //     at/pid 포함)로 연다.
  // - PC(웹) 알림: 웹에서도 가능 → '나' 탭 위에서 실제 알림 설정을 연다(설문 제거).
  //   atom="mypage"(탭=나) + /notification-settings 안착. PCSideTab 의 '나 섹션' 가드가
  //   /mypage 로 덮지 않고, Home.tsx 가드가 /progress 리다이렉트도 회피한다.
  // - PC(웹) 앱잠금: 모바일 전용 기능 → 안내 팝업만(설문 유지).
  const openSettingSeated = (open: () => void, taskKey: string) => {
    (taskKey === "notification-settings"
      ? trackNotificationLink
      : trackAppLockLink)();
    if (isInappWebview) {
      open(); // 네이티브 설정 스크린이 설문 위로 슬라이드(전환을 덮음)
      navigate(-1); // 가려진 webview 의 설문만 닫아 FAQ '환불이 가능한가요?' 상세로 복귀
    } else if (taskKey === "notification-settings") {
      setPCSideTab("mypage");
      navigate("/notification-settings", { replace: true });
    } else {
      setMobileOnlyModal({
        title: "모바일 앱에서 설정할 수 있어요.",
        subtitle:
          "모바일 앱에서 잠금 기능을 설정하여 내 정보를 안전하게 보호하세요.",
      });
    }
  };

  // 채널톡을 열고 가려진 설문만 닫는다(바닥=FAQ 상세). 오버레이가 전환을 덮어 깜빡임 없음.
  const openChatSeated = () => {
    trackChatLink();
    if (isInappWebview) {
      openChat(); // 전역 오버레이(ChannelTalkProvider) — route 변경에도 생존
      navigate(-1); // 오버레이 아래에서 설문만 닫아 FAQ '환불이 가능한가요?' 상세로 복귀
    } else {
      // PC: 홈으로 닫고(탭=홈, 콘텐츠=progress 일치) 그 위에 채널톡 오버레이.
      close();
      openChat();
    }
  };

  // 일시정지 링크: (구)채널톡 대신 일시정지 예약 페이지(/subscription-pause)로 이동.
  // 설정 링크와 동일 패턴으로 'FAQ상세 복귀'를 맞춘다.
  // - 웹뷰: openTask 로 예약 화면을 별도 네이티브 화면으로 push(인증 at/pid 포함)하고,
  //   그 아래 가려진 webview 의 설문만 navigate(-1) 로 닫는다. 예약 완료/뒤로 시 설문을
  //   띄웠던 FAQ '환불이 가능한가요?' 상세가 드러난다. (같은 webview 를 replace 하면
  //   예약 화면의 goBack=navigate_back 이 webview 를 통째로 닫아 고객센터로 떨어질 수 있어
  //   설정과 동작이 달라지므로 별도 화면으로 연다.)
  // - PC: 네이티브 스택이 없으므로 설문 자리를 replace 로 예약 화면으로 대체 → goBack
  //   navigate(-1) 이 FAQ상세로 복귀.
  const openPauseReservation = () => {
    trackPauseLink();
    if (isInappWebview) {
      openPauseTask();
      navigate(-1);
    } else {
      navigate("/subscription-pause", { replace: true });
    }
  };

  const reasons = getReasons(
    () => openSettingSeated(openAppLock, "security-settings"),
    () => openSettingSeated(openNotification, "notification-settings"),
    openPauseReservation,
    openChatSeated
  );

  // 뒤로가기: 안내 화면이면 사유 선택 리스트로, 리스트(step0)면 직전 페이지로.
  // step0 의 직전은 설문을 push 한 화면(FAQ '환불이 가능한가요?' 상세)이므로
  // navigate(-1) 로 같은 webview 내부 SPA 히스토리를 한 칸 되돌린다.
  // (useClose 의 webview navigate_back 은 webview 를 통째로 pop 해 FAQ 상세를
  //  건너뛰고 고객센터 리스트로 떨어지므로 쓰지 않는다.)
  const handleBack = () => {
    if (selectedKey !== null) {
      setSelectedKey(null);
      setTappedKey(null); // step0 복귀 시 직전 탭 강조 잔상 제거
    } else {
      navigate(-1);
    }
  };

  const selectedReason = reasons.find((r) => r.key === selectedKey);
  const isEtc = selectedKey === ETC_KEY;

  // step 0: 환불 사유 단일 선택 리스트
  if (selectedKey === null) {
    return (
      <FullPageWithHeaderAndProgressV2
        hideProgress={true}
        showClose={false}
        onBack={handleBack}
        body={
          <Stack spacing={5}>
            <CustomTypography
              variant="Subtitle"
              fontWeight={500}
              sx={{ color: theme.vars.palette.Content.Strong }}
            >
              {"어떤 이유로 환불을 받고자 하시나요?"}
            </CustomTypography>
            <Stack spacing={1}>
              {[...reasons, { key: ETC_KEY, label: "기타" }].map((reason) => (
                <Button
                  key={reason.key}
                  fullWidth
                  variant="SingleSlim"
                  size="lg"
                  color={tappedKey === reason.key ? "primary" : "secondary"}
                  sx={{ transition: "background-color 0.3s ease" }}
                  onClick={() => {
                    setTappedKey(reason.key);
                    trackSelectReason({
                      reason_key: reason.key,
                      reason_label: reason.label,
                    });
                    setTimeout(() => setSelectedKey(reason.key), 100);
                  }}
                >
                  {reason.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        }
      />
    );
  }

  // step 1: 사유별 안내 화면 또는 기타 자유 입력
  return (
    <>
      <FullPageWithHeaderAndProgressV2
        hideProgress={true}
        showClose={false}
        onBack={handleBack}
        body={
          <Box sx={{ paddingBottom: "120px" }}>
            {isEtc ? (
              <Stack spacing={5}>
                <CustomTypography
                  variant="Subtitle"
                  fontWeight={500}
                  sx={{ color: theme.vars.palette.Content.Strong }}
                >
                  {"다른 이유가 있으신가요?"}
                </CustomTypography>
                <Box
                  sx={{
                    width: "100%",
                    height: "200px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "start",
                    justifyContent: "start",
                    boxShadow: `inset 0 0 0 1.4px ${theme.vars.palette.Border.Weak}`,
                  }}
                >
                  <TextareaRefund
                    initialValue={etcText}
                    handleChange={(newValue: string) => setEtcText(newValue)}
                    isFocused={etcFocused}
                    setIsFocused={setEtcFocused}
                    placeholder={
                      "환불을 고민하게 된 이유가 있다면 편하게 알려주세요."
                    }
                  />
                </Box>
              </Stack>
            ) : (
              selectedReason?.content
            )}
          </Box>
        }
      />
      <BottomDangerButton
        onClick={() => {
          // 기타는 step0 선택 시점엔 자유 입력값이 비어 있으므로, 접수 시점에 실제
          // 입력한 사유 텍스트를 접수 이벤트로 함께 전달한다.
          if (isEtc) {
            trackSubmitReason({
              reason_key: ETC_KEY,
              reason_label: "기타",
              etc_text: etcText,
            });
          }
          setShowEmailModal(true);
        }}
        text={"환불 신청 접수하기"}
      />
      <CustomModal
        open={showEmailModal}
        onClose={handleExit}
        title={decision.title}
        subtitle={decision.subtitle}
        actions={[{ label: "확인", onClick: handleExit, variant: "Solid" }]}
      />
      {/* PC(웹): 앱잠금/알림 설정 링크 안내 — 모바일 앱에서 설정하도록. 설문은 유지. */}
      <CustomModal
        open={mobileOnlyModal !== null}
        onClose={() => setMobileOnlyModal(null)}
        title={mobileOnlyModal?.title ?? ""}
        subtitle={mobileOnlyModal?.subtitle ?? ""}
        actions={[
          {
            label: "확인",
            onClick: () => setMobileOnlyModal(null),
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}
